import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DollarCircleOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { Flex, Space } from "antd";
import { createStyles } from "antd-style";

import { toApplicationCloseDate } from "@av/apps/recruitment/ats-common/application-close-date";
import { JobLocationArrangment } from "@av/apps/recruitment/ats-common/components/JobLocationArrangment";
import { JobPostAddress } from "@av/apps/recruitment/ats-common/components/jobs/JobPostAddress";
import { JobPostCompensation } from "@av/apps/recruitment/ats-common/components/jobs/JobPostCompensation";
import { getLocalizedJobPostContent } from "@av/apps/recruitment/ats-common/localized-job-post-content";
import { Button } from "@av/components/atoms/Button";
import { Card } from "@av/components/atoms/Card";
import { Typography } from "@av/components/atoms/typography/Typography";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";
import { ViewJobDescriptionDrawer } from "./view-job-description/ViewJobDescriptionDrawer";
import { usePublicFetchSingleJobPost } from "../../../../services/public-job-posts/public-job-posts";

type JobApplicationBannerProps = {
  companyId: string;
  jobPostId: string;
};

const useStyles = createStyles(({ css, token }) => ({
  banner: css`
    background: ${token.gold1};
    padding: 0px;
  `,
  titleWrapper: css`
    flex: 1 1 0;
    min-width: 0;
  `,
  title: css`
    width: 300px;
  `,
  metaItem: css`
    display: inline-flex;
    align-items: center;
    gap: ${token.marginXXS}px;
  `,
  compensationMeta: css`
    white-space: nowrap;
  `,
  icons: css`
    color: ${token["blue-6"]};
  `,
  descriptionLink: css`
    color: ${token.colorPrimary};
    flex-shrink: 0;
  `,
}));

export function JobApplicationBanner({ companyId, jobPostId }: JobApplicationBannerProps) {
  const { styles } = useStyles();
  const { Text, Title } = Typography;
  const { t } = useTranslation();
  const { data: jobPost } = usePublicFetchSingleJobPost(companyId, jobPostId);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const testIdPrefix = "job-application-banner";
  const { locale } = useLocaleStore();
  const jobTitle = getLocalizedJobPostContent(jobPost?.jobTitles, locale);

  if (!jobPost) {
    return null;
  }

  const { minCompensation, maxCompensation, applyBy } = jobPost;

  return (
    <Card className={styles.banner} testIdPrefix={`${testIdPrefix}-container`}>
      <Flex align="center" justify="space-between" gap="middle">
        <Flex align="center" className={styles.titleWrapper} gap={32}>
          <Title
            level={2}
            className={styles.title}
            ellipsis={{ rows: 2, tooltip: jobTitle }}
            testIdPrefix={`${testIdPrefix}-title`}
          >
            {jobTitle}
          </Title>
          <Flex gap={32} wrap>
            <Flex vertical gap={2}>
              <Text className={styles.metaItem} testIdPrefix={`${testIdPrefix}-location`}>
                <EnvironmentOutlined size={14} className={styles.icons} />
                <JobLocationArrangment
                  locationType={jobPost.locationType}
                  city={jobPost.city}
                  subdivisionCode={jobPost.subdivisionCode}
                  employmentType={jobPost.employmentType}
                />
              </Text>
              <JobPostAddress
                city={jobPost.city}
                streetAddress={jobPost.streetAddress}
                subdivisionCode={jobPost.subdivisionCode}
                testIdPrefix={testIdPrefix}
              />
            </Flex>
            {jobPost.includeCompensationOnJobPost && (
              <Text className={styles.metaItem} testIdPrefix={`${testIdPrefix}-salary`}>
                <DollarCircleOutlined className={styles.icons} />
                <Flex gap={4} className={styles.compensationMeta}>
                  <JobPostCompensation
                    minCompensation={minCompensation}
                    maxCompensation={maxCompensation}
                    payDisplay={jobPost.payDisplay}
                    currencyCode={jobPost.currencyCode}
                    payType={jobPost.payType}
                    locale={locale}
                    testIdPrefix={testIdPrefix}
                  />
                  <Text testIdPrefix={`${testIdPrefix}-pay-type`}>
                    /{" "}
                    {jobPost.payType === "SALARY"
                      ? t("recruitment.jobs.addJob_compensation_frequencyUnits_SALARY")
                      : t("recruitment.jobs.addJob_compensation_frequencyUnits_HOURLY")}
                  </Text>
                </Flex>
              </Text>
            )}
            {applyBy && (
              <Text className={styles.metaItem} testIdPrefix={`${testIdPrefix}-deadline`}>
                <HistoryOutlined className={styles.icons} />
                {t("recruitment.jobApplication.acceptingApplicationsUntil", {
                  date: toApplicationCloseDate(applyBy)?.format(t("common.components.dateFormat")),
                })}
              </Text>
            )}
          </Flex>
        </Flex>
        <Button
          onClick={() => {
            setIsDrawerOpen(true);
          }}
          type="link"
          className={styles.descriptionLink}
          testIdPrefix={`${testIdPrefix}-description`}
        >
          <Space size={8} className={styles.descriptionLink}>
            <FileTextOutlined />
            {t("recruitment.jobApplication.jobDetails")}
          </Space>
        </Button>
      </Flex>

      <ViewJobDescriptionDrawer
        companyId={companyId}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        jobPost={jobPost}
      />
    </Card>
  );
}
